'use client';

import { ErrorSection } from '@pickleballinc/react-ui';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';

import { usePlayerTeamLeagues } from '@/hooks/players/useGetPlayerTeamLeagues';
import { FilterTabs } from '@/modules/players/v2/navigation/FilterTabs';
import { EventCardsListSkeleton } from '@/modules/players/v2/navigation/skeletons/EventCardsListSkeleton';
import { PlayerTeamLeaguesWrapper } from '@/modules/players/v2/tabs/team-leagues/PlayerTeamLeaguesWrapper';
import { PlayerData } from '@/types/players.types';
import { TeamLeagueStatus, TeamLeagueTypes } from '@/types/playerTeamLeagues.types';

interface TeamLeaguesClientWrapperProps {
  player: PlayerData;
  params: { slug: string };
  activeTeamLeagueType: TeamLeagueTypes;
  status: TeamLeagueStatus;
  searchParams: {
    [key: string]: string;
  };
}

export const TeamLeaguesClientWrapper = ({
  player,
  params,
  activeTeamLeagueType,
  status,
  searchParams,
}: TeamLeaguesClientWrapperProps) => {
  const { session } = useSession();

  const {
    data: teamLeaguesResult,
    isLoading,
    isFetching,
  } = usePlayerTeamLeagues({
    playerId: player.uuid,
    playerSlug: params.slug,
    activeTeamLeagueType,
    status,
    searchParams,
  });

  if (!isLoading && teamLeaguesResult?.error) {
    return (
      <ErrorSection
        Subheading='Please reach out to our support to report this issue'
        heading='Something wrong happened'
      />
    );
  }

  const teamLeaguesData = teamLeaguesResult?.data;
  const hasTeamLeagues = teamLeaguesData ? teamLeaguesData.totalRecords > 0 : false;

  return (
    <>
      <FilterTabs hasData={hasTeamLeagues} type='teamLeague' />

      <div className='container mx-auto mb-8 !max-w-[996px] rounded-md xl:!px-0'>
        {isLoading || isFetching ? (
          <EventCardsListSkeleton />
        ) : (
          <PlayerTeamLeaguesWrapper
            teamLeagues={teamLeaguesData?.teamLeagues ?? []}
            player={player}
            user={session?.user}
            totalRecords={teamLeaguesData?.totalRecords ?? 0}
          />
        )}
      </div>
    </>
  );
};
